import {
  Component,
  Input,
  Output,
  OnInit,
  EventEmitter,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-table-row-editor',
  templateUrl: './table-row-editor.component.html',
  styleUrls: ['./table-row-editor.component.css'],
})
export class TableRowEditorComponent implements OnInit {
  @Input() activeColumns: string[] = [];
  @Input() columnDefaultValue: { [key: string]: any } = {};
  @Input() dropdownOptions: { [key: string]: any[] } = {};
  @Input() columnLabels: { [key: string]: string } = {}; 
  @Input() buttonLabel: boolean = false;
  @Input() dataList: any[] = [];
  @Input() nonEditableColumns: string[] = []; // List of non-editable columns
  @Input() hasFilterList: { [key: string]: any[] } = {};
  @Input() canAdd:any;
  @Input() canEdit:any;
  @Input() canDelete:any;

  @Input() form: any = {};

  @Output() onSave: EventEmitter<any> = new EventEmitter<any>(); // Event for adding new row
  @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>(); // Event for updating existing row
  @Output() onDelete: EventEmitter<any> = new EventEmitter<any>(); // Event for deleting a row
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>(); // Event for deleting a row

  editRowId: number | null = null;
  isAddingNewRow: boolean = false;
  newRow: any = {};

  // Inject in constructor
  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.isAddingNewRow = false;
    // Initialize default row if dataList is present
    if (this.dataList.length > 0) {
      this.initDefaultRow();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataList']) {
      this.isAddingNewRow = false;
    }

    if (changes['editRowId']) {
      this.cdr.detectChanges();
    }
  }

  // Initialize the new row with default values
  initDefaultRow() {
    let _self = this;
    this.newRow = {};
    this.activeColumns.forEach((col) => {
      if (!this.newRow[col]) {
        if (
          _self.columnDefaultValue[col] !== undefined &&
          _self.columnDefaultValue[col] !== null
        ) {
          this.newRow[col] = _self.columnDefaultValue[col];
        } else {
          this.newRow[col] = this.getDataType(col, '') === 'select' ? null : ''; // Set default based on type
        }
      }
    });
  }

  getDefaultValueForSelect(key: string): any {
    const options = this.dropdownOptions[key];
    return options && options.length ? options[0].value : null;
  }

  public getInputError(name: string) {
    let _self = this;
    let errMsg = '';

    if (_self.form) {
      let ie = _self.form['inputerror'];
      if (ie && ie[name]) {
        errMsg = ie[name];
      }
    }

    return errMsg;
  }

  private originalRowData: any = {}; // Store original row data

  enableEditMode(rowId: number) {
    this.editRowId = rowId;
    this.isAddingNewRow = false;
    this.form['inputerror'] = {};

  // Find the row being edited
  const row = this.dataList.find((data) => data.id === rowId);
  if (row) {
    this.originalRowData = { ...row }; // Store a copy of the original data
  }
  }

  // Add a new row, preparing the template for user input
  addNewRow() {
    this.initDefaultRow();
    this.isAddingNewRow = true;
    this.editRowId = null;

    this.cdr.detectChanges();
    this.form['inputerror'] = {};
  }

  getSelected(selectValue: any, option: any) {
    if (option.value) {
      if (selectValue === option.value) {
        return true;
      }
    } else {
      if (selectValue === option) {
        return true;
      }
    }

    return false;
  }

//  Save the current row (either new or existing)
  save(row: any) {
  let _self = this;

  if (this.isAddingNewRow) {
    
     this.onSave.emit({
      row,
      callback: (isSaved: boolean) => {
        if (isSaved) {
          if (_self.isAddingNewRow) {
            _self.isAddingNewRow = false; 
          } else {
            _self.editRowId = null; 
          }
          _self.detectChange(); 
        } else {
          console.error('Save failed, keeping row in edit mode.');
        }
      },
    });
  } else {
   
    const rowIndex = this.dataList.findIndex((data) => data.id === row.id);
    if (rowIndex !== -1) {
      this.dataList[rowIndex] = { ...row }; 
      this.detectChange(); 
    }
    this.onUpdate.emit({
      row,
      callback: (isUpdated: boolean) => {
        if (isUpdated) {
          console.log('Row updated successfully');
          this.editRowId = null; 
          this.detectChange(); 
        } else {
          console.error('Update failed, keeping row in edit mode.');
  }
      },
    });
    this.detectChange(); // ✅ Ensure UI reflects the update
  }
}  cancel() {
  if (this.editRowId !== null) {
    const rowIndex = this.dataList.findIndex((data) => data.id === this.editRowId);

    if (rowIndex !== -1) {
      this.dataList[rowIndex] = { ...this.originalRowData };
    }
  }

  // Reset edit mode
    this.editRowId = null;
    this.isAddingNewRow = false;
    this.detectChange();
  }
  // Delete a row
  deleteRow(row: any) {
    this.onDelete.emit(row); // Emit delete event for the row
  }

  // Detect the data type of a specific field based on its value or configuration
  getDataType(key: string, value: any) {
    if (this.dropdownOptions[key]) return 'select';
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    return 'unknown'; // Keep 'unknown' at the end for better performance
  }

  detectChange() {
    var data = [...this.dataList];
    this.dataList = [];
    this.cdr.detectChanges();
    this.dataList = data;
    this.cdr.detectChanges();
    this.editRowId = null;
    this.isAddingNewRow = false;
  }

  // Get the custom label for a column or fallback to the column key
  getColumnLabel(key: string): string {
    return this.columnLabels[key] || key;
  }

  // Retrieve the display value for dropdowns when not in edit mode
  getDisplayValue(key: string, value: any): any {
    if (this.dropdownOptions[key]) {
      const option = this.dropdownOptions[key].find(
        (opt) => opt === value || opt?.value === value
      );
      return option?.name || option || value; // Return name if it's an object, or option itself if it's a string
    }
    return value;
  }

  // Check if the column should be non-editable
  isNonEditable(columnKey: string): boolean {
    return this.nonEditableColumns.includes(columnKey);
  }

  // Handle any changes in the new row without clearing other fields
  handleChange(key: any) {
    console.log('Row Updated:', this.newRow);
    let data = {
      change: key,
      data: this.newRow,
    };
    this.onChange.emit(data);
  }
}
